import { memo } from 'react';

import { WorkspaceAgentTagPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentTagPill';
import { type AgentElicitationDisplaySampleItem } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

type DisplaySampleTags = Extract<AgentElicitationDisplaySampleItem, { type: 'tags' }>;

export type WorkspaceAgentTagChangesTableProps = {
  caption: string | undefined;
  rows: readonly DisplaySampleTags[];
};

/** Tag writes add tags rather than change a value, so the columns are the asset and its new tags. */
export const WorkspaceAgentTagChangesTable = memo(({ caption, rows }: WorkspaceAgentTagChangesTableProps) => {
  if (!rows.length) {
    return null;
  }

  return (
    <div className={caption ? 'space-y-2' : undefined} data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY_TAGS">
      <section
        aria-label="Tags preview"
        className="max-h-64 overflow-y-auto"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be focusable for keyboard scrolling
        tabIndex={0} // NOSONAR -- scrollable region must be focusable for keyboard scrolling
      >
        <table className="w-full table-fixed text-left text-12">
          <thead className="sticky top-0 bg-gray-1">
            <tr className="border-gray-3 border-b">
              <th className="py-2 pr-3 font-medium text-gray-9 leading-4" scope="col">
                Title
              </th>
              <th className="py-2 font-medium text-gray-9 leading-4" scope="col">
                New tags
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                className="border-gray-3 border-b last:border-b-0"
                key={`${index}-${row.title}`} // NOSONAR -- titles collide, so the index keeps two assets from sharing a row
              >
                <td className="break-all py-2 pr-3 align-top font-mono text-gray-12 leading-4">{row.title}</td>
                <td className="py-1.5 align-top">
                  <div className="flex flex-wrap gap-1">
                    {row.tags.map((tag) => (
                      <WorkspaceAgentTagPill key={tag} label={tag} />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {caption ? <p className="text-12 text-gray-9 leading-4">{caption}</p> : null}
    </div>
  );
});

WorkspaceAgentTagChangesTable.displayName = 'WorkspaceAgentTagChangesTable';
